import { QueryClient } from '@tanstack/react-query';
import { Client, PaginatedResponse, Project } from '../types';
import { AI_ASSISTED_SETUP_ENABLED } from './aiSuggestionsEngine';

type ClientListCache = PaginatedResponse<Client>;
type ProjectListCache = PaginatedResponse<Project>;

function upsertClientList(old: ClientListCache | undefined, client: Client): ClientListCache {
  const pagination = old?.pagination || { page: 1, limit: 50, total: 0, pages: 1 };
  const data = old?.data || [];
  if (data.some((c) => c.id === client.id)) {
    return {
      data: data.map((c) => (c.id === client.id ? { ...c, ...client } : c)),
      pagination,
    };
  }
  return {
    data: [client, ...data],
    pagination: {
      ...pagination,
      total: (pagination.total || data.length) + 1,
    },
  };
}

function upsertProjectList(old: ProjectListCache | undefined, project: Project): ProjectListCache {
  const pagination = old?.pagination || { page: 1, limit: 50, total: 0, pages: 1 };
  const data = old?.data || [];
  if (data.some((p) => p.id === project.id)) {
    return {
      data: data.map((p) => (p.id === project.id ? { ...p, ...project } : p)),
      pagination,
    };
  }
  return {
    data: [project, ...data],
    pagination: {
      ...pagination,
      total: (pagination.total || data.length) + 1,
    },
  };
}

function removeFromClientList(
  old: ClientListCache | undefined,
  clientId: string,
): ClientListCache | undefined {
  if (!old?.data?.length) return old;
  const data = old.data.filter((c) => c.id !== clientId);
  if (data.length === old.data.length) return old;
  return {
    data,
    pagination: {
      ...old.pagination,
      total: Math.max(0, (old.pagination?.total ?? old.data.length) - 1),
    },
  };
}

function removeFromProjectList(
  old: ProjectListCache | undefined,
  projectId: string,
): ProjectListCache | undefined {
  if (!old?.data?.length) return old;
  const data = old.data.filter((p) => p.id !== projectId);
  if (data.length === old.data.length) return old;
  return {
    data,
    pagination: {
      ...old.pagination,
      total: Math.max(0, (old.pagination?.total ?? old.data.length) - 1),
    },
  };
}

function removeProjectsByClientFromList(
  old: ProjectListCache | undefined,
  clientId: string,
): ProjectListCache | undefined {
  if (!old?.data?.length) return old;
  const data = old.data.filter((p) => (p.clientId || p.client?.id) !== clientId);
  if (data.length === old.data.length) return old;
  const removed = old.data.length - data.length;
  return {
    data,
    pagination: {
      ...old.pagination,
      total: Math.max(0, (old.pagination?.total ?? old.data.length) - removed),
    },
  };
}

/** Immediately merge a new/updated client into sidebar + list caches. */
export function mergeClientIntoSidebarCache(queryClient: QueryClient, client: Client): void {
  queryClient.setQueriesData<ClientListCache>({ queryKey: ['wf-clients'] }, (old) =>
    upsertClientList(old, client)
  );
  queryClient.setQueriesData<ClientListCache>({ queryKey: ['clients'] }, (old) =>
    upsertClientList(old, client)
  );
  queryClient.setQueriesData<ClientListCache>({ queryKey: ['clients-for-project-form'] }, (old) =>
    upsertClientList(old, client)
  );
}

/** Immediately merge a new/updated project into sidebar caches. */
export function mergeProjectIntoSidebarCache(queryClient: QueryClient, project: Project): void {
  queryClient.setQueriesData<ProjectListCache>({ queryKey: ['wf-projects'] }, (old) =>
    upsertProjectList(old, project)
  );
  queryClient.setQueriesData<ProjectListCache>({ queryKey: ['projects'] }, (old) =>
    upsertProjectList(old, project)
  );
}

/** Immediately remove a client from sidebar + list caches. */
export function removeClientFromSidebarCache(queryClient: QueryClient, clientId: string): void {
  queryClient.setQueriesData<ClientListCache>({ queryKey: ['wf-clients'] }, (old) =>
    removeFromClientList(old, clientId)
  );
  queryClient.setQueriesData<ClientListCache>({ queryKey: ['clients'] }, (old) =>
    removeFromClientList(old, clientId)
  );
  queryClient.setQueriesData<ClientListCache>({ queryKey: ['clients-for-project-form'] }, (old) =>
    removeFromClientList(old, clientId)
  );
  queryClient.setQueriesData<ProjectListCache>({ queryKey: ['wf-projects'] }, (old) =>
    removeProjectsByClientFromList(old, clientId)
  );
  queryClient.setQueriesData<ProjectListCache>({ queryKey: ['projects'] }, (old) =>
    removeProjectsByClientFromList(old, clientId)
  );
}

/** Immediately remove a project from sidebar caches. */
export function removeProjectFromSidebarCache(queryClient: QueryClient, projectId: string): void {
  queryClient.setQueriesData<ProjectListCache>({ queryKey: ['wf-projects'] }, (old) =>
    removeFromProjectList(old, projectId)
  );
  queryClient.setQueriesData<ProjectListCache>({ queryKey: ['projects'] }, (old) =>
    removeFromProjectList(old, projectId)
  );
}

/** Refetch sidebar queries so server data replaces optimistic cache. */
export async function refetchWorkflowPanelQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.refetchQueries({ queryKey: ['wf-clients'] }),
    queryClient.refetchQueries({ queryKey: ['wf-projects'] }),
    queryClient.refetchQueries({ queryKey: ['wf-admin-has-clients'] }),
    queryClient.refetchQueries({ queryKey: ['wf-agency-managed-clients'] }),
    queryClient.refetchQueries({ queryKey: ['wf-agency-all-clients'] }),
    queryClient.refetchQueries({ queryKey: ['wf-agency-project-count'] }),
    queryClient.refetchQueries({ queryKey: ['wf-agency-self-projects'] }),
    queryClient.invalidateQueries({ queryKey: ['wf-client-project-count'] }),
  ]);
}

/** After restoring archived items: refresh workflow sidebar and list caches immediately. */
export async function completeArchiveRestore(queryClient: QueryClient): Promise<void> {
  await refetchWorkflowPanelQueries(queryClient);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['projects'] }),
    queryClient.invalidateQueries({ queryKey: ['clients'] }),
    queryClient.invalidateQueries({ queryKey: ['clients-for-project-form'] }),
    queryClient.invalidateQueries({ queryKey: ['client-projects'] }),
    queryClient.invalidateQueries({ queryKey: ['publishers'] }),
    // Order Status / placements (so restore shows without a manual page refresh)
    queryClient.invalidateQueries({ queryKey: ['placements'] }),
    queryClient.invalidateQueries({ queryKey: ['placement-status-stats'] }),
    queryClient.invalidateQueries({ queryKey: ['guestBlogPlacements'] }),
    queryClient.invalidateQueries({ queryKey: ['guestBlogOrders'] }),
    queryClient.invalidateQueries({ queryKey: ['all-placements'] }),
    queryClient.invalidateQueries({ queryKey: ['ordered-sites'] }),
    queryClient.invalidateQueries({ queryKey: ['guest-blog-sites'] }),
    queryClient.invalidateQueries({ queryKey: ['project-reporting-placements-list'] }),
    queryClient.invalidateQueries({ queryKey: ['project-reporting-link-status'] }),
    queryClient.invalidateQueries({ queryKey: ['project-reporting'] }),
    queryClient.invalidateQueries({ queryKey: ['project-overview-all-placements'] }),
    queryClient.invalidateQueries({ queryKey: ['wf-step-placements'] }),
    queryClient.invalidateQueries({ queryKey: ['live-link-statuses'] }),
    queryClient.invalidateQueries({ queryKey: ['live-link-stats'] }),
  ]);
}

/** After archiving any entity: refresh My Archive / Restore Center lists immediately. */
export function invalidateArchiveRestoreQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['deletion-restore-records'] });
  void queryClient.invalidateQueries({ queryKey: ['deletion-restore-entity-types'] });
}

/** After creating a client: update sidebar caches immediately and open/focus panel. */
export function notifyClientCreated(
  queryClient: QueryClient,
  client: Client,
  focusClientInPanel?: (clientId: string) => void
): void {
  mergeClientIntoSidebarCache(queryClient, client);
  focusClientInPanel?.(client.id);
}

/** After creating a project: update sidebar caches immediately. */
export function notifyProjectCreated(
  queryClient: QueryClient,
  project: Project
): void {
  mergeProjectIntoSidebarCache(queryClient, project);
  const clientId = project.clientId || project.client?.id || '';
  markOnboardingHasProjects(queryClient, clientId || undefined);
}

/** Onboarding gates use project-count queries — update optimistically so navigation isn't blocked. */
export function markOnboardingHasProjects(
  queryClient: QueryClient,
  clientId?: string
): void {
  const ensureHasProjects = (old: ProjectListCache | undefined): ProjectListCache => {
    const prevTotal = old?.pagination?.total ?? 0;
    if (prevTotal > 0 && old) return old;
    return {
      data: old?.data ?? [],
      pagination: {
        page: old?.pagination?.page ?? 1,
        limit: old?.pagination?.limit ?? 1,
        pages: 1,
        total: Math.max(1, prevTotal),
      },
    };
  };

  queryClient.setQueryData(['wf-agency-project-count'], ensureHasProjects);
  if (clientId) {
    queryClient.setQueryData(['wf-client-project-count', clientId], ensureHasProjects);
  }
}

/** Close project dialog, refresh caches in background, then enter workflow after modal exit. */
export function completeProjectCreation(
  queryClient: QueryClient,
  project: Project,
  callbacks: {
    closeDialog: () => void;
    focusClientInPanel?: (clientId: string) => void;
    selectProject?: (project: Project, clientName: string, colorIndex?: number) => void;
    clientName?: string;
    /** After select, open AI-assisted setup wizard (ignored while AI_ASSISTED_SETUP_ENABLED is false). */
    openAiSetup?: boolean;
    navigate?: (path: string) => void;
  }
): void {
  callbacks.closeDialog();
  notifyProjectCreated(queryClient, project);
  void refetchWorkflowPanelQueries(queryClient);
  void queryClient.invalidateQueries({ queryKey: ['projects'] });

  const clientId = project.clientId || project.client?.id || '';
  const clientName = callbacks.clientName || project.client?.name || 'Client';
  // Master flag off → always go to Explore Sites (skip Setup Method screen).
  const openAiSetup = AI_ASSISTED_SETUP_ENABLED && callbacks.openAiSetup !== false;

  // Let the MUI dialog backdrop finish its exit transition before navigating.
  window.setTimeout(() => {
    if (clientId) callbacks.focusClientInPanel?.(clientId);
    callbacks.selectProject?.(project, clientName, 0);
    if (clientId && callbacks.navigate) {
      if (openAiSetup) {
        callbacks.navigate(
          `/project-workflow/ai-setup?projectId=${encodeURIComponent(project.id)}&clientId=${encodeURIComponent(clientId)}`,
        );
      } else {
        callbacks.navigate(
          `/sites?projectId=${encodeURIComponent(project.id)}&clientId=${encodeURIComponent(clientId)}`,
        );
      }
    }
  }, 250);
}

/** After deleting a client: remove from sidebar immediately and clear workflow if needed. */
export function completeClientDeletion(
  queryClient: QueryClient,
  clientId: string,
  callbacks?: {
    clearProject?: () => void;
    selectedClientId?: string | null;
    selectedProjectClientId?: string | null;
    onCleared?: () => void;
  },
): void {
  removeClientFromSidebarCache(queryClient, clientId);
  void refetchWorkflowPanelQueries(queryClient);
  void queryClient.invalidateQueries({ queryKey: ['clients'] });
  void queryClient.invalidateQueries({ queryKey: ['client-projects', clientId] });
  void queryClient.invalidateQueries({ queryKey: ['wf-client-project-count'] });
  invalidateArchiveRestoreQueries(queryClient);

  const affectsSelection =
    callbacks?.selectedClientId === clientId ||
    callbacks?.selectedProjectClientId === clientId;

  if (affectsSelection) {
    callbacks?.clearProject?.();
    callbacks?.onCleared?.();
  }
}

/** After deleting a project: remove from sidebar immediately and clear workflow if needed. */
export function completeProjectDeletion(
  queryClient: QueryClient,
  projectId: string,
  callbacks?: {
    clearProject?: () => void;
    selectedProjectId?: string | null;
    onCleared?: () => void;
  },
): void {
  removeProjectFromSidebarCache(queryClient, projectId);
  void refetchWorkflowPanelQueries(queryClient);
  void queryClient.invalidateQueries({ queryKey: ['projects'] });
  void queryClient.invalidateQueries({ queryKey: ['wf-client-project-count'] });
  invalidateArchiveRestoreQueries(queryClient);

  if (callbacks?.selectedProjectId === projectId) {
    callbacks?.clearProject?.();
    callbacks?.onCleared?.();
  }
}

/** After updating a client: refresh sidebar caches and workflow labels immediately. */
export function completeClientUpdate(
  queryClient: QueryClient,
  client: Client,
  callbacks?: {
    patchClientInSelection?: (clientId: string, name: string) => void;
  },
): void {
  mergeClientIntoSidebarCache(queryClient, client);
  void refetchWorkflowPanelQueries(queryClient);
  callbacks?.patchClientInSelection?.(client.id, client.name);
}

/** After updating a project: refresh sidebar caches and workflow labels immediately. */
export function completeProjectUpdate(
  queryClient: QueryClient,
  project: Project,
  callbacks?: {
    patchProjectInSelection?: (projectId: string, projectName: string, clientName?: string) => void;
    clientName?: string;
  },
): void {
  mergeProjectIntoSidebarCache(queryClient, project);
  void refetchWorkflowPanelQueries(queryClient);
  void queryClient.invalidateQueries({ queryKey: ['projects'] });
  void queryClient.invalidateQueries({ queryKey: ['project-overview', project.id] });
  // Re-run Ahrefs Overview analysis for the (possibly new) website URL
  void queryClient.invalidateQueries({ queryKey: ['project-overview-seo', project.id] });
  const clientName = callbacks?.clientName || project.client?.name;
  callbacks?.patchProjectInSelection?.(project.id, project.projectName, clientName);
  // Cart & checkout reads budget from cart API — refresh so Monthly budget updates immediately.
  window.dispatchEvent(
    new CustomEvent('wf-project-updated', {
      detail: {
        id: project.id,
        budget: project.budget != null ? Number(project.budget) : null,
        projectName: project.projectName,
      },
    }),
  );
}
